import {
  AppShell as MantineAppShell,
  Notification,
  Transition,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconExclamationCircle, IconX } from "@tabler/icons-react";
import {
  useAppShellLayout,
  useLoading,
  useNotificationWindow,
} from "../../stores/stores";
import CommonLoader from "./CommonLoader";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [opened, { toggle, close }] = useDisclosure();
  const isMobile = useMediaQuery("(max-width: 991px)") ?? false;

  const {
    type,
    isNotificationShow,
    notificationMessage,
    setIsNotificationShow,
  } = useNotificationWindow();

  const { isSidebarCollapsed } = useAppShellLayout();
  const { isLoading } = useLoading();

  return (
    <MantineAppShell
      header={{ height: 56 }}
      navbar={{
        width: {
          base: 240,
          sm: 240,
          md: isSidebarCollapsed ? 60 : 240,
          lg: isSidebarCollapsed ? 60 : 240,
          xl: isSidebarCollapsed ? 60 : 240,
        },
        breakpoint: 992,
        collapsed: { mobile: !opened, desktop: false },
      }}
      padding={0}
      className="h-[calc(100vh-55px)] mt-[55px] bg-[#1A1A1A] overflow-hidden"
      styles={{
        navbar: {
          transition: "width 300ms ease, transform 300ms ease",
          transform:
            opened || !isMobile ? "translateX(0)" : "translateX(-100%)",
        },
        main: {
          transition: "padding-left 300ms ease",
        },
      }}
    >
      <Header opened={opened} onToggle={toggle} isMobile={isMobile} />

      <div className="overflow-hidden rounded-2xl rounded-b-none h-[calc(100vh-56px)]">
        {opened && (
          <div
            className="absolute inset-0 bg-[#1a1a1a77] z-[99]"
            onClick={close}
          >
            <div className="absolute w-[30px] top-0 bottom-0 bg-[#1a1a1a] z-[99]"></div>
          </div>
        )}
        <Sidebar
          isMobile={isMobile}
          opened={opened}
          onClose={close}
          onToggle={toggle}
        />

        <MantineAppShell.Main
          className="bg-gray-50 overflow-y-auto pt-0"
          style={{
            paddingTop: 0,
          }}
        >
          <Transition mounted={isNotificationShow} transition="slide-left">
            {(transitionStyle) => (
              <div
                className="fixed right-5 z-[999999999]"
                style={transitionStyle}
              >
                <Notification
                  style={{ zIndex: 9999999 }}
                  icon={
                    (type === "success" && <IconCheck color="#fff" />) ||
                    (type === "failure" && <IconX color="#fff" />) || (
                      <IconExclamationCircle color="#fff" />
                    )
                  }
                  color={
                    (type === "failure" && "red") ||
                    (type === "success" && "teal") ||
                    "orange"
                  }
                  title={notificationMessage}
                  mt="md"
                  onClose={() => {
                    setIsNotificationShow(false);
                  }}
                  withBorder
                ></Notification>
              </div>
            )}
          </Transition>
          <div className="h-[calc(100vh-56px)] bg-white overflow-y-auto">
            <div className="p-0 overflow-y-auto">{children}</div>
          </div>
          {isLoading && <CommonLoader />}
        </MantineAppShell.Main>
      </div>
    </MantineAppShell>
  );
}
